import { baseApi } from './baseApi';

/**
 * Products API endpoints
 * Handles CRUD operations for product management
 */
export const productsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /products - Fetch all products with pagination
    getProducts: builder.query({
      query: ({ showDeleted, page, limit, manufacturerId } = {}) => {
        const params = new URLSearchParams();
        if (showDeleted !== undefined) params.append('showDeleted', showDeleted);
        if (page !== undefined) params.append('page', page);
        if (limit !== undefined) params.append('limit', limit);
        if (manufacturerId !== undefined && manufacturerId !== null) params.append('manufacturer_id', manufacturerId);
        return `/products?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.products
          ? [
              ...result.products.map(({ product_id }) => ({ type: 'Product', id: product_id })),
              { type: 'Product', id: 'LIST' },
            ]
          : [{ type: 'Product', id: 'LIST' }],
    }),

    // GET /products/:id - Fetch product by ID
    getProductById: builder.query({
      query: (id) => `/products/${id}`,
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),

    // POST /products - Create new product
    createProduct: builder.mutation({
      query: (productData) => ({
        url: '/products',
        method: 'POST',
        body: productData,
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    // PATCH /products/:id - Update product
    updateProduct: builder.mutation({
      query: ({ id, product_id, ...productData }) => ({
        url: `/products/${id || product_id}`,
        method: 'PATCH',
        body: productData,
      }),
      invalidatesTags: (result, error, { id, product_id }) => [
        { type: 'Product', id: id || product_id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    // DELETE /products/:id - Delete product (soft delete)
    deleteProduct: builder.mutation({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    // GET /products/:productId/images - Get product images
    getProductImages: builder.query({
      query: (productId) => `/products/${productId}/images`,
      providesTags: (result, error, productId) => [{ type: 'ProductImage', id: productId }],
    }),

    // POST /products/:productId/images - Upload product image
    uploadProductImage: builder.mutation({
      query: ({ productId, formData }) => ({
        url: `/products/${productId}/images`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: 'ProductImage', id: productId },
        { type: 'Product', id: productId },
      ],
    }),

    // DELETE /products/:productId/images/:imageId - Delete product image
    deleteProductImage: builder.mutation({
      query: ({ productId, imageId }) => ({
        url: `/products/${productId}/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: 'ProductImage', id: productId },
        { type: 'Product', id: productId },
      ],
    }),

    // POST /products/:productId/images/:imageId/primary - Set primary image
    setPrimaryProductImage: builder.mutation({
      query: ({ productId, imageId }) => ({
        url: `/products/${productId}/images/${imageId}/primary`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: 'ProductImage', id: productId },
        { type: 'Product', id: productId },
      ],
    }),

    // POST /products/:productId/images/unset-primary - Unset primary image
    unsetPrimaryProductImage: builder.mutation({
      query: (productId) => ({
        url: `/products/${productId}/images/unset-primary`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, productId) => [
        { type: 'ProductImage', id: productId },
        { type: 'Product', id: productId },
      ],
    }),

    // GET /products/:productId/metadata - Get product metadata
    getProductMetadata: builder.query({
      query: (productId) => `/products/${productId}/metadata`,
      providesTags: (result, error, productId) => [{ type: 'ProductMetadata', id: productId }],
    }),

    // GET /products/:productId/documents - Get product documents
    getProductDocuments: builder.query({
      query: (productId) => `/products/${productId}/documents`,
      providesTags: (result, error, productId) => [{ type: 'ProductDocument', id: productId }],
    }),

    // PUT /products/:productId/metadata - Update product metadata
    updateProductMetadata: builder.mutation({
      query: ({ productId, metadata }) => ({
        url: `/products/${productId}/metadata`,
        method: 'PUT',
        body: metadata,
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: 'ProductMetadata', id: productId },
        { type: 'Product', id: productId },
      ],
    }),

    // GET /products/:productId/pieces - Get pieces for product
    getProductPieces: builder.query({
      query: (productId) => `/products/${productId}/pieces`,
      providesTags: (result, error, productId) => [
        { type: 'Piece', id: `PRODUCT_${productId}` },
        ...(result || []).map(({ piece_id }) => ({ type: 'Piece', id: piece_id })),
      ],
    }),

    // GET /products/:productId/translations - Get product translations
    getProductTranslations: builder.query({
      query: (productId) => `/products/${productId}/translations`,
      providesTags: (result, error, productId) => [{ type: 'ProductTranslation', id: productId }],
    }),

    // GET /products?search=query - Search products
    searchProducts: builder.query({
      query: ({ search, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (limit) params.append('limit', limit);
        return `/products?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.products
          ? [
              ...result.products.map(({ product_id }) => ({ type: 'Product', id: product_id })),
              { type: 'Product', id: 'SEARCH' },
            ]
          : [{ type: 'Product', id: 'SEARCH' }],
    }),

    // POST /product-imports/preview - Preview manufacturer JSON import
    previewProductImport: builder.mutation({
      query: (formData) => ({
        url: '/product-imports/preview',
        method: 'POST',
        body: formData,
      }),
    }),

    // POST /product-imports/confirm - Confirm manufacturer JSON import
    confirmProductImport: builder.mutation({
      query: (payload) => ({
        url: '/product-imports/confirm',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'Product', id: 'LIST' },
      ],
    }),

    previewWebshopSync: builder.mutation({
      query: (payload) => ({
        url: '/webshop-sync/preview',
        method: 'POST',
        body: payload,
      }),
    }),

    confirmWebshopSync: builder.mutation({
      query: (payload) => ({
        url: '/webshop-sync/confirm',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'Product', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetProductImagesQuery,
  useUploadProductImageMutation,
  useDeleteProductImageMutation,
  useSetPrimaryProductImageMutation,
  useUnsetPrimaryProductImageMutation,
  useGetProductMetadataQuery,
  useGetProductDocumentsQuery,
  useUpdateProductMetadataMutation,
  useGetProductPiecesQuery,
  useGetProductTranslationsQuery,
  useSearchProductsQuery,
  usePreviewProductImportMutation,
  useConfirmProductImportMutation,
  usePreviewWebshopSyncMutation,
  useConfirmWebshopSyncMutation,
} = productsApi;

